import { StrategyDailyMetric, DeployMarkerItem } from '../../../../types/Ad.api.type';
import { Card } from '../../../ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';

type DataSource = 'TOSS' | 'ONLYME';

interface KpiChartProps {
  title: string;
  data: StrategyDailyMetric[];
  dataKey: keyof StrategyDailyMetric;
  dataKey2?: keyof StrategyDailyMetric;
  dataKey3?: keyof StrategyDailyMetric;
  dataKey4?: keyof StrategyDailyMetric;
  color: string;
  color2?: string;
  color3?: string;
  color4?: string;
  label1?: string;
  label2?: string;
  label3?: string;
  label4?: string;
  dashed3?: boolean;
  height?: number;
  goal?: number;
  deploys?: DeployMarkerItem[];
  source?: DataSource;
}

export const KpiChart = ({
  title,
  data,
  dataKey,
  dataKey2,
  dataKey3,
  dataKey4,
  color,
  color2,
  color3,
  color4,
  label1,
  label2,
  label3,
  label4,
  dashed3,
  height = 200,
  goal,
  deploys,
  source,
}: KpiChartProps) => {
  const chartData = data.map((d) => ({
    date: d.date.slice(5),
    fullDate: d.date,
    [dataKey]: d[dataKey],
    ...(dataKey2 ? { [dataKey2]: d[dataKey2] } : {}),
    ...(dataKey3 ? { [dataKey3]: d[dataKey3] } : {}),
    ...(dataKey4 ? { [dataKey4]: d[dataKey4] } : {}),
  }));

  const lastVal = [...data].reverse().find((d) => d[dataKey] != null)?.[dataKey];
  const isAboveGoal = goal != null && lastVal != null && Number(lastVal) >= goal;

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
            {source && (
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  source === 'TOSS' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                }`}
              >
                {source}
              </span>
            )}
          </div>
          {goal != null && (
            <span
              className={`text-xs font-mono px-2 py-0.5 rounded ${isAboveGoal ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}
            >
              {`목표: ${goal} | 현재: ${lastVal ?? '-'}`}
            </span>
          )}
        </div>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            {(dataKey2 || dataKey3 || dataKey4) && <Legend />}
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
              name={label1 ?? title}
            />
            {dataKey2 && color2 && (
              <Line
                type="monotone"
                dataKey={dataKey2}
                stroke={color2}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
                name={label2 ?? String(dataKey2)}
              />
            )}
            {dataKey3 && color3 && (
              <Line
                type="monotone"
                dataKey={dataKey3}
                stroke={color3}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
                name={label3 ?? String(dataKey3)}
                strokeDasharray={dashed3 ? '5 5' : undefined}
              />
            )}
            {dataKey4 && color4 && (
              <Line
                type="monotone"
                dataKey={dataKey4}
                stroke={color4}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
                name={label4 ?? String(dataKey4)}
              />
            )}
            {goal != null && (
              <ReferenceLine
                y={goal}
                stroke="#9ca3af"
                strokeDasharray="5 5"
                label={{ value: `목표 ${goal}`, position: 'right', fontSize: 10, fill: '#9ca3af' }}
              />
            )}
            {deploys?.map((d) => {
              const shortDate = d.date.slice(5);
              return (
                <ReferenceLine
                  key={d.date}
                  x={shortDate}
                  stroke="#6b7280"
                  strokeDasharray="3 3"
                  label={{ value: d.version, position: 'top', fontSize: 9, fill: '#6b7280' }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};
